import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'
import {AgendaUrlParams} from '@/features/agenda/types'
import {httpGetPatient} from '@/features/patients/services/patients.http'
import {PatientSmall} from '@/features/patients/types'
import {httpGetUser} from '@/features/users/http/users.http'
import {UserSmall} from '@/features/users/types'
import {QUERY_KEYS} from '@/queryClient'
import {useQueries} from '@tanstack/react-query'

type AgendaSubjectsResult<T extends AgendaUrlParams['entity']> = {
    subjects: T extends 'patients' ? PatientSmall[] : UserSmall[]
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
}

export const useAgendaCategoryCustom = <T extends AgendaUrlParams['entity']>(): AgendaSubjectsResult<T> => {
    const {
        searchParams: {subjectIds}
    } = useAgendaSearchParams()
    const {
        params: {entity}
    } = useAgendaNavigation()

    const isEnabledQuery = subjectIds?.length && subjectIds.length > 0
    const subjects = subjectIds?.length ? subjectIds : []

    const result = useQueries({
        queries: subjects.map(subjectId => ({
            queryKey:
                entity == 'users' ? [QUERY_KEYS.USERS, subjectId, 'small'] : [QUERY_KEYS.PATIENTS, subjectId, 'small'],
            queryFn: () =>
                entity == 'users'
                    ? httpGetUser({urlParams: {userId: subjectId}, params: {responseType: 'small'}})
                    : httpGetPatient({
                          urlParams: {patientId: subjectId},
                          params: {responseType: 'small'}
                      }),
            options: {
                enabled: isEnabledQuery && (entity == 'users' || entity == 'patients')
            }
        })),
        combine: data => {
            const subjects = data.map(result => result.data).filter(Boolean)
            return {
                subjects: subjects as UserSmall[] | PatientSmall[],
                isLoading: data.some(result => result.isLoading),
                isError: data.some(result => result.isError),
                isSuccess: data.every(result => result.isSuccess)
            }
        }
    })

    return result as AgendaSubjectsResult<T>
}
